import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import realtorApiService from '../services/realtorApiService';

interface BrokerOfRecord {
  memberKey: string;
  firstName: string;
  lastName: string;
  fullName: string;
  officeKey: string;
  officeName: string;
  phone: string;
  province: string;
  city: string;
  mlsId: string;
}

const BrokerOfRecordList: React.FC = () => {
  const [brokers, setBrokers] = useState<BrokerOfRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvince, setFilterProvince] = useState<string>('all');
  const [sortField, setSortField] = useState<'fullName' | 'province' | 'officeName'>('fullName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all offices from Ontario and Alberta first to get office names efficiently
      const officeFilter = "(OfficeStateOrProvince eq 'Ontario' or OfficeStateOrProvince eq 'Alberta') and OfficeStatus eq 'Active'";
      const allOffices = await realtorApiService.getAllOffices({
        $filter: officeFilter,
        $select: ['OfficeKey', 'OfficeName']
      });

      // Create office name map
      const officeMap = new Map<string, string>();
      allOffices.forEach(office => {
        if (office.OfficeKey && office.OfficeName) {
          officeMap.set(office.OfficeKey, office.OfficeName);
        }
      });

      // Fetch all Broker of Record members from Ontario and Alberta, only active
      const memberFilter = "(MemberStateOrProvince eq 'Ontario' or MemberStateOrProvince eq 'Alberta') and MemberStatus eq 'Active' and JobTitle eq 'Broker of Record'";
      const allMembers = await realtorApiService.getAllMembers({
        $filter: memberFilter
      });

      // Filter out members with no office
      const membersWithOffice = allMembers.filter(m => m.OfficeKey);

      // Transform to BrokerOfRecord format and add office names
      const brokersList: BrokerOfRecord[] = membersWithOffice.map(member => ({
        memberKey: member.MemberKey || '',
        firstName: member.MemberFirstName || '',
        lastName: member.MemberLastName || '',
        fullName: `${member.MemberFirstName || ''} ${member.MemberLastName || ''}`.trim(),
        officeKey: member.OfficeKey || '',
        officeName: officeMap.get(member.OfficeKey || '') || 'N/A',
        phone: member.MemberOfficePhone || '',
        province: member.MemberStateOrProvince || '',
        city: member.MemberCity || '',
        mlsId: member.MemberMlsId || ''
      }));

      setBrokers(brokersList);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data from DDF API');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  const filteredBrokers = useMemo(() => {
    let filtered = [...brokers];

    // Search by name
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(broker => 
        broker.fullName.toLowerCase().includes(searchLower) ||
        broker.firstName.toLowerCase().includes(searchLower) ||
        broker.lastName.toLowerCase().includes(searchLower)
      );
    }

    // Filter by province
    if (filterProvince !== 'all') {
      filtered = filtered.filter(broker => broker.province === filterProvince);
    }

    return filtered;
  }, [brokers, searchTerm, filterProvince]);

  // Sort
  const sortedBrokers = useMemo(() => {
    const sorted = [...filteredBrokers];
    sorted.sort((a, b) => {
      let aValue: string = '';
      let bValue: string = '';

      if (sortField === 'fullName') {
        aValue = a.fullName;
        bValue = b.fullName;
      } else if (sortField === 'province') {
        aValue = a.province;
        bValue = b.province;
      } else if (sortField === 'officeName') {
        aValue = a.officeName;
        bValue = b.officeName;
      }

      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredBrokers, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedBrokers.length / itemsPerPage);
  const paginatedBrokers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedBrokers.slice(start, start + itemsPerPage);
  }, [sortedBrokers, currentPage]);

  const handleSort = (field: 'fullName' | 'province' | 'officeName') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const exportToCSV = () => {
    const headers = [
      'First Name',
      'Last Name',
      'Full Name',
      'Member Key',
      'MLS ID',
      'Office Name',
      'Office Key',
      'Phone',
      'City',
      'Province'
    ];

    const rows = sortedBrokers.map(broker => [
      broker.firstName,
      broker.lastName,
      broker.fullName,
      broker.memberKey,
      broker.mlsId,
      broker.officeName,
      broker.officeKey,
      broker.phone,
      broker.city,
      broker.province
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `brokers_of_record_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (field: 'fullName' | 'province' | 'officeName') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpIcon className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDownIcon className="h-4 w-4 inline ml-1" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Brokers of Record | BizBuyMax.com</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Brokers of Record</h1>
                <p className="text-gray-600 mt-1">
                  Ontario & Alberta - {sortedBrokers.length} brokers
                </p>
              </div>
              <button
                onClick={exportToCSV}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={filterProvince}
                onChange={(e) => {
                  setFilterProvince(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Provinces</option>
                <option value="Ontario">Ontario</option>
                <option value="Alberta">Alberta</option>
              </select>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
              <button
                onClick={loadData}
                className="mt-2 text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading brokers from DDF API...</p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('fullName')}
                      >
                        Name {getSortIcon('fullName')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Member Key
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MLS ID
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('officeName')}
                      >
                        Office Name {getSortIcon('officeName')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Office Key
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        City
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('province')}
                      >
                        Province {getSortIcon('province')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedBrokers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                          No brokers found
                        </td>
                      </tr>
                    ) : (
                      paginatedBrokers.map((broker) => (
                        <tr key={broker.memberKey} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {broker.fullName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {broker.memberKey}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {broker.mlsId || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {broker.officeName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {broker.officeKey}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {broker.phone || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {broker.city || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {broker.province}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedBrokers.length)} of {sortedBrokers.length} brokers
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-2 text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BrokerOfRecordList;
